/**
 * Liquid can only see TOTAL store inventory: `store_availabilities` carries a boolean per location, no
 * quantity, so one unit at the chosen store and forty across the chain look identical. This asks the
 * app proxy for the real figure at the resolved store and lowers every cap on the page to it.
 *
 * The resolved store also reaches checkout validation, so it checks the same store the buyer was quoted
 * against — as a cart attribute for pickup, and as a line property for delivery. See
 * `syncPickupAttribute` for why the two sit at different levels. Being theme JS, it writes nothing on
 * the paths that never run it — cart permalinks and Storefront API carts.
 */
(() => {
  const ENDPOINT = '/apps/fps-integrations/api/inventory-availability';
  const PICKUP_COOKIE = 'locationDrawerSelected';
  const ADDRESS_COOKIE = 'addressDrawerResult';
  const QUANTITY_INPUTS = 'input[data-quantity-variant-id]';
  const VARIANT_PICKERS = 'select[name="id"], input[name="id"]';
  const PICKUP_PROPERTY = 'properties[_pickup_location_id]';
  const DELIVERY_PROPERTY = 'properties[_delivery_location_id]';
  const DELIVERY_ZIP_PROPERTY = 'properties[_delivery_zip]';
  const PICKUP_ATTRIBUTE = '_pickup_location_id';
  // An accelerated checkout button builds its checkout from the form and never touches the cart, so a
  // form carrying one is the only place the pickup store still rides the line.
  const ACCELERATED_CHECKOUT = 'shopify-accelerated-checkout, .shopify-payment-button';
  const PRODUCT_FORMS =
    "form[is='product-form'], form[ref='main-pdp-form'], form[is='product-bundle-selection'], form[action*='/cart/add']";
  const CART_SURFACES = 'cart-items, cart-drawer';
  // A card can own a picker and a stepper without a <form> of its own — the bundle card associates its
  // inputs by `form=` instead. These are the containers that scope one card's controls together.
  const CARD_SCOPES = 'product-bundle-variant-selector, .product-bundle-card, quantity-input, .sticky-atc-bar';
  const REFRESH_DELAY = 150;
  // Long enough for the endpoint to walk a routing hierarchy for a batch, short enough that a buyer
  // is not left waiting on a request that is never coming back.
  const REQUEST_TIMEOUT = 8000;
  // The endpoint rejects a larger `items` array outright, and a 400 drops the cap for every variant in
  // the group — a long cart or a quick-order list reaches this on its own.
  const BATCH_LIMIT = 50;

  let inFlight = null;
  let refreshTimer = null;
  /** Bumped per refresh, so a slow response can tell whether it still speaks for the page. */
  let generation = 0;
  /** One cart write at a time — see syncPickupAttribute. */
  let pickupWriteChain = Promise.resolve();
  let pickupWritesPending = 0;
  /** Cart quantities per variant, read from /cart.js when the page cannot be trusted to show them. */
  let cartHeldByVariant = null;
  /** The pickup store the cart currently carries, as `/cart.js` last reported or this file last wrote. */
  let cartPickupLocationId = null;
  /** Whether `cartPickupLocationId` has been read from the cart yet, rather than merely assumed. */
  let pickupAttributeSeeded = false;
  /**
   * Set while a reconciliation refresh is already queued after a failed pickup write, so a burst schedules
   * one retry rather than a fresh refresh per failure — and a reconciliation that fails again cannot
   * re-arm, which would storm a persistently failing `/cart/update.js`. Cleared once a write persists.
   */
  let pickupReconcileScheduled = false;
  /** The app's last deliverable flag per postcode, so a previewed postcode already answered is not asked again. */
  const knownDeliverable = new Map();
  /** The drawer-preview verdict request, superseded by the next preview. */
  let previewInFlight = null;

  /**
   * `fetch` that cannot hang. A stalled request is the one failure the catch blocks below never see:
   * the gate `scheduleRefresh` closed stays closed, and Add to cart never comes back for the life of
   * the page.
   *
   * Rejects with a TimeoutError, deliberately NOT an AbortError — an abort means a newer refresh has
   * taken over and this one should die quietly, while a timeout has to fall through to fail-open.
   */
  function fetchWithTimeout(url, options = {}, outerSignal = null) {
    const controller = new AbortController();
    const timer = setTimeout(
      () => controller.abort(new DOMException('Availability request timed out', 'TimeoutError')),
      REQUEST_TIMEOUT,
    );
    const forward = () => controller.abort(outerSignal.reason);

    if (outerSignal) {
      if (outerSignal.aborted) forward();
      else outerSignal.addEventListener('abort', forward, { once: true });
    }

    return fetch(url, { ...options, signal: controller.signal }).finally(() => {
      clearTimeout(timer);
      outerSignal?.removeEventListener('abort', forward);
    });
  }

  function readCookie(name) {
    const prefix = `${name}=`;
    const entry = (document.cookie ? document.cookie.split('; ') : []).find(c => c.startsWith(prefix));
    if (!entry) return null;

    try {
      return JSON.parse(decodeURIComponent(entry.substring(prefix.length)));
    } catch (error) {
      return null;
    }
  }

  // google-maps-geocoder.js stores address_components already parsed into a flat object, so the
  // postcode is a plain field rather than a Google component array.
  //
  // Cookies written before that parsing existed carry only `formatted_address`, and they outlive the
  // deploy by weeks of Max-Age. Reading the postcode back out of the formatted address keeps those
  // buyers capped instead of silently skipping delivery for them. Anchored on the state code, because
  // "the first five digits" matches a street number just as happily.
  function deliveryZip() {
    return postcodeOf(readCookie(ADDRESS_COOKIE));
  }

  function postcodeOf(address) {
    const parsed = address?.address_components?.postal_code;

    if (typeof parsed === 'string' && parsed.trim() !== '') return parsed.trim();

    const formatted = typeof address?.formatted_address === 'string' ? address.formatted_address : '';
    const fallback = formatted.match(/\b[A-Z]{2}\s+(\d{5})(?:-\d{4})?\b/);

    return fallback ? fallback[1] : null;
  }

  /**
   * Which store to ask about for ONE input or form: the chosen pickup store, or the postcode a delivery
   * goes to (the app walks the same routing hierarchy an order would).
   *
   * Resolved per element, never once per page. A cart can hold a pickup line and a delivery line, and a
   * quick-view can be set to a different method than the PDP behind it.
   */
  function contextFor(element) {
    const lineMethod = element.dataset?.deliveryMethod;

    // A cart line that carries no method is untagged — it was never added through the store picker, so
    // there is no store to judge it against and it must not borrow the page's.
    if (lineMethod !== undefined && lineMethod.trim() === '') return null;

    const form = formOf(element);
    const method = (
      lineMethod ||
      form?.querySelector('input[name="properties[_delivery_method]"]')?.value ||
      ''
    )
      .trim()
      .toLowerCase();

    // A delivery line already knows the store routing resolved for it, and it is not necessarily the
    // store another line resolved to. A pickup line carries none, and follows the drawer.
    const lineLocationId = element.dataset?.fulfilmentLocation?.trim();
    if (method === 'delivery' && lineLocationId) return { locationId: lineLocationId };

    const selected = pickupLocationId();
    const pickup = selected ? { locationId: selected } : null;
    const zip = deliveryZip();
    const delivery = zip ? { zip } : null;

    if (method === 'delivery') return delivery;
    if (method === 'pickup') return pickup;

    return pickup ?? delivery;
  }

  /**
   * The drawer's chosen store, as the id the app keys figures on.
   *
   * The cookie has two historical shapes, the same two `checkout-delivery-preference.js` and
   * `cart-fulfilment-location.js` read: a legacy flat object, and a per-method one. Null means nothing
   * is selected, which is not the same as a selection this file failed to understand.
   */
  function pickupLocationId() {
    const raw = readCookie(PICKUP_COOKIE);

    if (!raw) return null;

    const selection = raw.latitude === undefined && raw.pickup ? raw.pickup : raw;

    return numericId(selection?.shopifyLocationId);
  }

  /**
   * The trailing numeric part of a Shopify id, normalised the way the checkout validation normalises
   * what it reads. A cookie the buyer can edit must not put junk on the cart, where it would reach the
   * order and then quietly fail open.
   */
  function numericId(raw) {
    const value = String(raw ?? '').trim();
    const id = value.slice(value.lastIndexOf('/') + 1);

    return /^\d+$/.test(id) ? id : null;
  }

  /** The variant a form will submit, which is not always the one its markup was rendered with. */
  function selectedVariantId(scope) {
    const picker = scope?.querySelector(VARIANT_PICKERS);

    return picker?.value?.trim() || null;
  }

  /**
   * The cart line an input edits. `data-index` identifies the line, not the input — main-cart renders
   * two inputs per line under the same index. Only read inside a cart surface: a quick-order row keys
   * the same attribute by variant.
   */
  function lineKey(input) {
    return input.closest(CART_SURFACES) ? (input.dataset.index ?? null) : null;
  }

  /** The cart line's item key, stable even when a sibling line's removal re-indexes the rest. */
  function lineItemKey(input) {
    return input.closest(CART_SURFACES) ? (input.dataset.key ?? null) : null;
  }

  /** A quick-order row and a cart line submit an ABSOLUTE quantity; a product form adds on top. */
  function isAbsoluteQuantity(input) {
    return input.name === 'updates[]';
  }

  function chunked(items, size) {
    const chunks = [];

    for (let start = 0; start < items.length; start += size) {
      chunks.push(items.slice(start, start + size));
    }

    return chunks;
  }

  function quantityInputs() {
    return [...document.querySelectorAll(QUANTITY_INPUTS)].filter(input => input.dataset.quantityVariantId);
  }

  /**
   * Point a picker's quantity input at the variant the form will actually submit, carrying that
   * variant's own cap and cart quantity when the picker publishes them.
   */
  function syncInputToPicker(picker) {
    // The form first, then the card that owns both controls: a bundle card has no form of its own and
    // wires its inputs up with `form=`, so its select must still reach its own stepper. Falling back to
    // the document would repoint — and uncap — an unrelated one, so a picker with neither is skipped.
    const scope = picker.form ?? picker.closest(`${PRODUCT_FORMS}, ${CARD_SCOPES}`);

    if (!scope) return;

    const input = scope.querySelector(QUANTITY_INPUTS);
    if (!input || !picker.value || input.dataset.quantityVariantId === picker.value) return;

    input.dataset.quantityVariantId = picker.value;

    // Only a <select> publishes per-variant figures. The PDP's picker is a hidden input, and the
    // theme's own updateQuantityRules refreshes its cap and cart quantity from the re-rendered
    // section — reading its absence of options as "unknown" would strip the PDP's ceiling and, on a
    // failed lookup, hold its Add to cart for good.
    if (picker.selectedOptions === undefined) return;

    const option = picker.selectedOptions[0];

    // Both figures on the input describe the variant it was rendered with. A picker that publishes
    // its own says what the new one holds; one that does not leaves us with a number for the wrong
    // variant, which would come off the new variant's stock — a cap too low, or too high. Unknown is
    // zero here: over-quoting is caught at checkout, under-quoting silently loses the sale.
    if (option?.dataset.cartQuantity) {
      input.dataset.cartQuantity = option.dataset.cartQuantity;
    } else if (option !== undefined) {
      delete input.dataset.cartQuantity;
    }

    const max = option?.dataset.maxQuantity;

    // An option with no `data-max-quantity` at all — the selection and bundle cards render none —
    // says nothing about the new variant, and the ceiling still on the input belongs to the old one.
    // Neither keeping it nor trusting it is safe, so the ceiling is unknown until the proxy answers,
    // and `capUnknown` is what stops the answer's absence being read as "no cap".
    if (max === undefined) {
      delete input.dataset.max;
      input.removeAttribute('max');
      input.dataset.capUnknown = 'true';
      return;
    }

    delete input.dataset.capUnknown;

    if (max === '') {
      delete input.dataset.max;
      input.removeAttribute('max');
    } else {
      input.dataset.max = max;
      input.max = max;
    }
  }

  /**
   * Units already committed, to net off a RAW stock figure. A product form adds ON TOP of the cart, so
   * its whole cart quantity is spent. An absolute-quantity input is not adding to anything: a cart
   * line's own quantity is already in the total (only its siblings count), and a quick-order row's
   * value IS the cart quantity.
   */
  function cartHeld(input, inputs) {
    if (input.closest(CART_SURFACES)) return siblingQuantity(input, inputs);
    if (isAbsoluteQuantity(input)) return 0;

    if (input.dataset.cartQuantity !== undefined) return parseInt(input.dataset.cartQuantity, 10) || 0;

    // A card whose picker publishes no per-variant cart quantity leaves none on the input. `/cart.js`
    // is the authority; the cart surfaces on the page are the fallback when that call fails, and they
    // are only a fallback because a theme without the drawer rendered has none to count.
    const variantId = input.dataset.quantityVariantId;

    return cartHeldByVariant?.get(variantId) ?? cartQuantityFromCartSurfaces(variantId, inputs);
  }

  /**
   * What the cart actually holds, per variant, and the pickup store it carries. The DOM can only answer
   * the first where a cart surface is rendered, and a theme with the drawer switched off has none — and
   * it can never answer the second, because a full-page cache would serve one buyer's store to another.
   * So ask the cart itself.
   */
  async function loadCart() {
    try {
      // Respect localized routing (e.g. /en/cart.js) like cartUpdateUrl does, rather than hardcoding /cart.js.
      const cartUrl = window.FoxTheme?.routes?.cart_url;
      const response = await fetchWithTimeout(cartUrl ? `${cartUrl}.js` : '/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) return null;

      const cart = await response.json();
      const held = new Map();

      for (const item of cart?.items ?? []) {
        const id = String(item.variant_id);
        held.set(id, (held.get(id) ?? 0) + (parseInt(item.quantity, 10) || 0));
      }

      return { held, pickupLocationId: numericId(cart?.attributes?.[PICKUP_ATTRIBUTE]) };
    } catch (error) {
      return null;
    }
  }

  function cartQuantityFromCartSurfaces(variantId, inputs) {
    const counted = new Set();
    let total = 0;

    for (const other of inputs) {
      if (!other.closest(CART_SURFACES)) continue;
      if (other.dataset.quantityVariantId !== variantId) continue;

      const key = other.dataset.index ?? null;
      if (key === null || counted.has(key)) continue;

      counted.add(key);
      total += parseInt(other.value, 10) || 0;
    }

    return total;
  }

  /**
   * What the OTHER cart lines already hold of this variant. The Liquid cap nets this off per line, and
   * a per-variant figure applied to every input would hand it straight back.
   */
  function siblingQuantity(input, inputs) {
    const key = lineKey(input);
    if (key === null) return 0;

    const counted = new Set([key]);
    let total = 0;

    for (const other of inputs) {
      const otherKey = lineKey(other);
      if (otherKey === null || counted.has(otherKey)) continue;
      if (other.dataset.quantityVariantId !== input.dataset.quantityVariantId) continue;

      counted.add(otherKey);
      total += parseInt(other.value, 10) || 0;
    }

    return total;
  }

  /** Cart baselines are pre-netted server-side; a product form's baseline is raw stock. */
  function resetToBaseline(input, inputs) {
    const baseline = parseInt(input.dataset.max, 10);
    if (!Number.isFinite(baseline)) return;

    const ceiling = input.closest(CART_SURFACES) ? baseline : baseline - cartHeld(input, inputs);

    input.max = String(Math.max(0, ceiling));
  }

  /** Drops a line's retry state along with the true pre-correction value it was stashed for. */
  function clearCapPending(input) {
    delete input.dataset.capPending;
    delete input.dataset.capPreValue;
    delete input.dataset.capPreMax;
  }

  function applyCap(input, available, inputs, dispatchedLines, corrections, correctedInputs) {
    // The stable server-rendered ceiling, not `input.max` — that attribute is what THIS function
    // rewrites every time it caps a line, so comparing a fresh cap against it compares the cap to a
    // previous refresh's own write. A `capPending` line whose correction was aborted would then look
    // "already covered" by its own optimistic value and lose its retry (FPS-731). `data-max` is never
    // touched by a correction, only by a variant change or the one-time capUnknown stamp below, so it
    // stays a true baseline to compare against — netted the same way `resetToBaseline` nets it, or a
    // product form's raw `data-max` (which is *not* pre-netted — see `resetToBaseline`) would compare
    // the cart-netted `capped` below against an inflated ceiling and let a location's raw stock figure
    // overwrite a lower merchant order-limit `resetToBaseline` had already correctly applied.
    const baseline = parseInt(input.dataset.max, 10);

    // No cap in the markup means Liquid decided this variant has none — untracked, or one the
    // merchant lets oversell. The endpoint has no business adding one.
    if (!Number.isFinite(baseline)) return;

    const held = cartHeld(input, inputs);
    const current = input.closest(CART_SURFACES) ? baseline : baseline - held;
    const capped = Math.max(0, available - held);
    if (capped >= current) {
      // A dangling `capPending` from an earlier, now-irrelevant cap: the ceiling that prompted it has
      // since lifted (more stock at the new store), so there is nothing left to retry.
      if (input.dataset.capPending === 'true') clearCapPending(input);
      return;
    }

    // `max` only. `data-max` stays the server-rendered baseline every refresh starts from.
    input.max = String(capped);

    // A `capPending` line's last correction request was aborted mid-flight rather than confirmed. That
    // abort proves nothing about whether the request reached Shopify (see `submitCorrections`), so a
    // value that merely matches the cap is not evidence the cart was actually corrected — retry it
    // instead of trusting it. A value strictly BELOW the cap is different: the shopper edited the line
    // again since, and forcing it back up to the cap would overwrite that newer, deliberate choice.
    const currentValue = parseInt(input.value, 10) || 0;
    const pending = input.dataset.capPending === 'true';
    if (currentValue < capped) return;
    if (currentValue === capped && !pending) return;

    // A cart line's value already IS the server's quantity, so lowering only the display leaves an
    // oversold line sitting in the cart until checkout. Record the correction by line item key; the
    // caller submits every correction from every group in this refresh as one request. Desktop/mobile
    // render two inputs per line under one index, so both values come down but only the first of the
    // pair is recorded.
    const index = lineKey(input);
    const key = lineItemKey(input);
    if (isAbsoluteQuantity(input) && index !== null && key !== null) {
      // Stashed only on the first attempt: a retry's own `input.value` is already the capped figure an
      // earlier attempt wrote, and capturing that as "previous" would roll a later failure back to the
      // cap instead of to what the cart is actually known to hold.
      if (!pending) {
        input.dataset.capPreValue = input.value;
        input.dataset.capPreMax = String(current);
      }

      correctedInputs.push({
        input,
        key,
        previousValue: input.dataset.capPreValue,
        previousMax: input.dataset.capPreMax,
        capped,
      });
      input.value = String(capped);
      input.dataset.capPending = 'true';
      if (dispatchedLines.has(index)) return;
      dispatchedLines.add(index);
      corrections.set(key, capped);
      return;
    }

    // A quick-order-list row is also an absolute-quantity input, but its own `change` handler reverts to
    // the original value on a step/min failure without ever submitting — lowering its displayed value
    // here would diverge the row from the server cart without fixing anything. Leave it; `max` already
    // blocks the stepper. (Out of this fix's scope — see the ticket's coverage-gap note.)
    if (isAbsoluteQuantity(input)) return;

    // A product form's value has to come down with the ceiling, or the buyer submits the quantity that
    // was showing before the store answered — nothing has been submitted yet, so writing it is enough.
    input.value = String(capped);
  }

  /**
   * Every cap correction this refresh needs, across every group, in one `/cart/update.js` call keyed by
   * line item key.
   *
   * @returns {Promise<'confirmed'|'unknown'|'failed'>} `'confirmed'` once the cart is known to reflect
   * the correction; `'failed'` if the caller must roll the optimistic DOM values back to match the cart
   * Shopify actually still holds; `'unknown'` when a newer refresh aborted this request, which can happen
   * after it already reached Shopify — an abort only stops the client waiting on the response, it proves
   * nothing about whether the server applied it. Treating that as success used to skip the rollback and
   * leave the DOM's optimistic value looking final even when the cart never actually changed; the
   * caller instead leaves the affected lines marked pending so the next refresh retries them.
   */
  async function submitCorrections(corrections, signal) {
    // `/cart/update.js`, as writePickupAttribute uses — not `routes.cart_update_url`, which is the form
    // route `/cart/update` (no `.js`). One endpoint for both cart writes keeps code and tests aligned.
    const url = cartUpdateUrl();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } }),
    );

    try {
      const response = await fetchWithTimeout(
        url,
        {
          ...window.FoxTheme?.utils?.fetchConfig?.(),
          body: JSON.stringify({ updates: Object.fromEntries(corrections), sections: sectionsToBundle }),
        },
        signal,
      );

      if (!response.ok) return 'failed';

      const parsedState = await response.json();
      window.FoxTheme?.pubsub?.publish?.(window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate, { cart: parsedState });
      return 'confirmed';
    } catch (error) {
      return error?.name === 'AbortError' ? 'unknown' : 'failed';
    }
  }

  /**
   * Nothing left at the resolved store: the form must not offer an Add to cart that submits zero.
   *
   * The gate has its own flag rather than borrowing `data-force-disable`. The theme sets that lock for
   * its own reasons — a web-hidden or sold-out variant — and a gate that took ownership of it would
   * release someone else's lock whenever the variant changed in between. The theme's re-enable paths
   * honour this flag alongside their own.
   */
  function gateAddToCart(form, soldOutHere) {
    for (const button of form.querySelectorAll('[type="submit"], button[name="add"]')) {
      if (soldOutHere) {
        button.dataset.locationGated = 'true';
        button.setAttribute('disabled', 'disabled');
      } else {
        releaseGate(button);
      }
    }
  }

  function releaseGate(button) {
    if (button.dataset.locationGated !== 'true') return;

    delete button.dataset.locationGated;

    // Whatever the theme locked for its own reasons stays locked.
    if (button.dataset.forceDisable !== 'true') {
      // Stock is only one of the button's gates — frequency/pickup/delivery selection (owned by
      // <delivery-options>) is another. Blindly removing `disabled` here re-enables the button even
      // when nothing has been selected yet (FPS-730). Defer to delivery-options' own re-evaluation
      // when this button's own form owns one, same as product-info.js's variant-change and
      // handleToggleATC paths do. formOf() (not `button.closest(...)`) resolves the owning form for
      // every shape in PRODUCT_FORMS, including form-attribute-associated buttons; delivery-options
      // is a sibling of that form (both children of the .product-form wrapper), not a descendant of
      // it, so the lookup still has to walk up to that wrapper before querying back down.
      const deliveryOptions = formOf(button)?.closest('.product-form')?.querySelector('delivery-options');
      if (deliveryOptions && typeof deliveryOptions.dispatchSubmitButtonUpdate === 'function') {
        deliveryOptions.dispatchSubmitButtonUpdate();
        return;
      }

      // Only the sticky ATC bar's own separate `.sticky-atc-bar__form` (scoped via the same
      // `.sticky-atc-bar` class CARD_SCOPES already recognizes) always mirrors the single main PDP
      // form -- sticky-atc-bar.js itself resolves that same `.main-product-form`. Every OTHER form
      // without a delivery-options of its own (a product card's quick-add, a bundle selector) is
      // unrelated to this product's fulfillment selection and must not be coupled to it.
      if (button.closest('.sticky-atc-bar')) {
        const mainSubmitButton = document.querySelector('.main-product-form [name="add"]');
        if (mainSubmitButton && mainSubmitButton !== button) {
          button.toggleAttribute('disabled', mainSubmitButton.hasAttribute('disabled'));
          return;
        }
      }

      button.removeAttribute('disabled');
    }
  }

  function releaseAllGates() {
    for (const button of document.querySelectorAll('[data-location-gated="true"]')) {
      releaseGate(button);
    }
  }

  /**
   * The resolved store, written on the form so the checkout validation checks the same one.
   *
   * Delivery is stamped on every form. Pickup is stamped only where an accelerated checkout button
   * would otherwise carry no store at all: that button builds its checkout straight from the form, so
   * the cart attribute this file maintains never reaches it. Everywhere else the pickup store stays off
   * the line, which is what stops it freezing at add-to-cart time and splitting one variant across two
   * lines.
   */
  function stampLocation(form, context, locationId) {
    clearFormStamps(form);

    if (!locationId) return;

    if (context.zip !== undefined) {
      addStamp(form, DELIVERY_PROPERTY, locationId);
      // Also the postcode it resolved for, so checkout can catch a stamp gone stale when the buyer
      // changes address after add-to-cart — a store that still has stock looks valid for any address.
      addStamp(form, DELIVERY_ZIP_PROPERTY, context.zip);
    } else if (form.querySelector(ACCELERATED_CHECKOUT)) {
      addStamp(form, PICKUP_PROPERTY, locationId);
    }
  }

  function addStamp(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  function clearFormStamps(form) {
    form.querySelector(`input[name="${PICKUP_PROPERTY}"]`)?.remove();
    form.querySelector(`input[name="${DELIVERY_PROPERTY}"]`)?.remove();
    form.querySelector(`input[name="${DELIVERY_ZIP_PROPERTY}"]`)?.remove();
  }

  function clearStamps() {
    for (const form of document.querySelectorAll(PRODUCT_FORMS)) {
      clearFormStamps(form);
    }
  }

  /**
   * The pickup store, written once on the cart rather than once per line.
   *
   * A cart has one pickup store: the drawer holds one, and checkout blocks a cart that mixes pickup
   * with delivery. Held per line it froze at add-to-cart time and disagreed with the drawer the moment
   * the buyer switched store — the staleness FPS-682 removed `_pickup_at` for. Rewritten here on every
   * change, it cannot drift, and it survives into the Storefront cart FPS-670 builds, which carries
   * cart attributes across.
   *
   * Delivery keeps its per-line stamp on purpose: the app resolves a delivery line's store per variant
   * by walking the routing hierarchy until one covers that line, so a single address really can split a
   * cart across two stores (FPS-700). One cart-wide value would name the wrong store for all but the
   * first line.
   *
   * The one pickup purchase this cannot reach is an accelerated checkout, which never touches the cart
   * — `stampLocation` covers that path on the form instead.
   */
  function syncPickupAttribute() {
    // Serialised, not merely ordered locally: two `/cart/update.js` calls in flight together can be
    // applied in either order, and the cart would then keep whichever request landed last rather than
    // whichever store the buyer chose last. Chained, each write starts only once the one before it has
    // settled — and reads the cookie at its own turn, so the last write in a burst always carries the
    // current choice and the ones it overtook collapse into no-ops.
    pickupWritesPending += 1;
    pickupWriteChain = pickupWriteChain.then(writePickupAttribute, writePickupAttribute);

    return pickupWriteChain;
  }

  async function writePickupAttribute() {
    try {
      const value = pickupLocationId();

      // Nothing selected. The attribute is left standing rather than cleared: closing the drawer must
      // not stop a cart full of pickup lines being judged, and a stale pickup store is ignored on a
      // delivery line anyway, because that line is judged by its own stamp.
      if (value === null) return;

      // Not read from the cart yet, so there is nothing to compare against. The first refresh seeds it.
      if (!pickupAttributeSeeded) return;

      // Already what the cart holds — the burst collapsed onto this value, or a reconciliation confirmed
      // it — so any queued reconcile is satisfied and must not keep re-arming.
      if (value === cartPickupLocationId) {
        pickupReconcileScheduled = false;
        return;
      }

      const response = await fetchWithTimeout(cartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [PICKUP_ATTRIBUTE]: value } }),
      });

      if (!response.ok) throw new Error(`cart/update.js ${response.status}`);

      // Only once the cart has actually taken it. Claiming it beforehand is what lets a failed write
      // leave this believing a store is persisted that never was.
      cartPickupLocationId = value;
      pickupReconcileScheduled = false;
    } catch (error) {
      // Drop the seed rather than guess: the reconciliation the finally schedules once the burst settles
      // re-reads what the cart really holds and writes again from there.
      pickupAttributeSeeded = false;
      cartPickupLocationId = null;
    } finally {
      pickupWritesPending -= 1;

      // The burst has fully drained, but a write in it failed — the seed was dropped — while a store is
      // still selected. The last selection may never have persisted: a later write in the burst
      // short-circuited on the cleared seed. Schedule one reconciliation so the next refresh re-reads the
      // cart and rewrites the current choice; guarded so a reconciliation that fails again cannot re-arm.
      if (pickupWritesPending === 0 && !pickupAttributeSeeded && !pickupReconcileScheduled && pickupLocationId() !== null) {
        pickupReconcileScheduled = true;
        scheduleRefresh(true);
      }
    }
  }

  /** True while no cart write is outstanding, so a cart read can be trusted to speak for the attribute. */
  function pickupWritesQuiet() {
    return pickupWritesPending === 0;
  }

  function cartUpdateUrl() {
    const cartUrl = window.FoxTheme?.routes?.cart_url;

    return cartUrl ? `${cartUrl}/update.js` : '/cart/update.js';
  }

  /**
   * Everything on the page that wants an answer: each quantity input, and each add-to-cart form —
   * cards and bundles have a form and a variant but no stepper, and they still need the store stamped
   * and the button gated when it holds none.
   */
  /** The form an element submits through — `form` honours a `form=` attribute, `closest` does not. */
  function formOf(element) {
    return element.form ?? element.closest(PRODUCT_FORMS);
  }

  function targets() {
    const inputs = quantityInputs();
    const targeted = [];
    const formsWithInputs = new Set();

    for (const input of inputs) {
      const form = formOf(input);
      if (form) formsWithInputs.add(form);

      targeted.push({ input, form, variantId: input.dataset.quantityVariantId, context: contextFor(input) });
    }

    for (const form of document.querySelectorAll(PRODUCT_FORMS)) {
      if (formsWithInputs.has(form)) continue;

      const variantId = selectedVariantId(form);
      if (!variantId) continue;

      targeted.push({ input: null, form, variantId, context: contextFor(form) });
    }

    return { inputs, targeted };
  }

  async function refresh() {
    const era = ++generation;

    document.querySelectorAll(VARIANT_PICKERS).forEach(syncInputToPicker);

    const { inputs, targeted } = targets();

    // Nothing to ask about — but scheduleRefresh may have closed the gate on the way in, and returning
    // without releasing it would leave Add to cart disabled for the life of the page.
    if (targeted.length === 0) {
      releaseAllGates();
      return;
    }

    // Only when a product form's own figure is missing — the PDP and the cart carry theirs in the
    // markup, so the usual page costs nothing extra.
    const needsCartQuantities = targeted.some(
      target =>
        target.input &&
        target.input.dataset.cartQuantity === undefined &&
        !target.input.closest(CART_SURFACES) &&
        !isAbsoluteQuantity(target.input),
    );

    // The cart is read on the first pass whatever the page needs, so the pickup store this file writes
    // is compared against the one the cart actually carries rather than assumed absent.
    const cart = needsCartQuantities || !pickupAttributeSeeded ? await loadCart() : null;
    const held = cart?.held ?? null;

    // Reconciled from the cart, not merely seeded once: a write that failed, or a cart that dropped its
    // attributes when its last line went, would otherwise leave this believing the store is written.
    // Only while no write is outstanding, or the read would be older than the value it overwrites.
    if (cart !== null && pickupWritesQuiet()) {
      pickupAttributeSeeded = true;
      cartPickupLocationId = cart.pickupLocationId;
      syncPickupAttribute();
    }

    // `/cart.js` carries no abort signal — aborting `inFlight` stops the availability call, not this
    // one — so two overlapping refreshes can resolve out of order and the older one would publish its
    // figures over the newer's. A newer refresh owns the page and will set every cap and gate itself,
    // so this one has nothing left to say.
    if (era !== generation) return;

    // A failed read keeps the last figures instead of dropping to none. `cartHeld` reads an absent map
    // as zero units held, which hands back a ceiling the buyer can add the store's entire stock on top
    // of what they already have. Figures one cart-change out of date are wrong by that change; none are
    // wrong by everything the cart holds. A real change publishes `cart:updated` and refreshes anyway.
    if (!needsCartQuantities) {
      cartHeldByVariant = null;
    } else if (held !== null) {
      cartHeldByVariant = held;
    }

    // One request per distinct store, not per target: a page is usually all one context, a mixed cart
    // is two.
    const groups = new Map();
    const unresolved = [];

    for (const target of targeted) {
      if (!target.context) {
        unresolved.push(target);
        continue;
      }

      const key = JSON.stringify(target.context);
      const group = groups.get(key) ?? { context: target.context, targets: [] };
      group.targets.push(target);
      groups.set(key, group);
    }

    // Anything that no longer resolves to a store must not keep the last one's cap, stamp or gate.
    for (const target of unresolved) {
      if (target.input) resetToBaseline(target.input, inputs);
      if (target.form) {
        stampLocation(target.form, {}, null);
        gateAddToCart(target.form, false);
      }
    }

    if (groups.size === 0) {
      clearStamps();
      releaseAllGates();
      return;
    }

    inFlight?.abort();
    const controller = new AbortController();
    inFlight = controller;

    // Shared across every group in this refresh, not per group: a mixed cart (pickup + delivery) forms
    // more than one group, and each used to fire its own /cart/update.js concurrently with the others.
    // Two in-flight requests race the same cart-section render the same way the original per-line bug
    // did — whichever response lands last overwrites the DOM with whatever it saw, even if that was a
    // snapshot older than a sibling group's already-applied correction. One request for the whole
    // refresh removes the race instead of moving it up a level.
    const corrections = new Map();
    const correctedInputs = [];

    try {
      await Promise.all(
        [...groups.values()].map(group => applyGroup(group, controller.signal, inputs, corrections, correctedInputs)),
      );

      // A fast group's correction now waits on every sibling group before this submits, which is a
      // longer window than before for the shopper to edit that same line through the cart's own
      // /cart/change.js path — a request this refresh neither starts nor waits for. Anything no longer
      // showing the value recorded here has already moved on under someone else's request; submitting
      // it anyway would overwrite that with this refresh's stale figure.
      const fresh = correctedInputs.filter(entry => (parseInt(entry.input.value, 10) || 0) === entry.capped);

      if (fresh.length < correctedInputs.length) {
        // A discarded entry's `capPending` (set by applyCap before this filter runs) must go with it:
        // left standing, a later refresh would see the marker, not the shopper's own newer edit, and
        // force the line back up to a cap that no longer describes what's being submitted here.
        for (const entry of correctedInputs) {
          if (!fresh.includes(entry)) clearCapPending(entry.input);
        }

        correctedInputs.length = 0;
        correctedInputs.push(...fresh);
        corrections.clear();
        for (const entry of fresh) corrections.set(entry.key, entry.capped);
      }

      if (corrections.size > 0) {
        // Order this refresh's two cart writes: concurrent writes make Shopify 503 (cart locked) and the
        // correction is the one lost, leaving a stale line (FPS-731). Chain never rejects; no-op if idle.
        await pickupWriteChain;
        // A newer refresh can supersede this one while it waits on that chain; bail before submitting this
        // store's now-stale caps (the post-submit guard only skips applying the outcome, not the request).
        if (era !== generation) return;
        const outcome = await submitCorrections(corrections, controller.signal);

        // A newer refresh may have started, and already own, while this one awaited the response —
        // its own generation is current now, and it will re-evaluate every one of these inputs itself.
        // Applying this outcome on top of that would fight a correction it has no knowledge of.
        if (era !== generation) return;

        if (outcome === 'confirmed') {
          for (const { input } of correctedInputs) clearCapPending(input);
        } else if (outcome === 'failed') {
          for (const { input, previousValue, previousMax } of correctedInputs) {
            input.value = previousValue;
            input.max = previousMax;
            clearCapPending(input);
          }
        }
        // 'unknown': leave the DOM and `capPending` as they are — applyCap retries a pending line on the
        // next refresh instead of trusting a request whose outcome nobody actually saw.
      }
    } finally {
      if (inFlight === controller) inFlight = null;
      // Re-runs validateQtyRules, which is what disables `+` at the new ceiling. No cart write.
      window.FoxTheme?.pubsub?.publish?.(window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.quantityUpdate);
    }
  }

  async function applyGroup(group, signal, allInputs, corrections, correctedInputs) {
    const { context, targets: groupTargets } = group;
    const variantIds = [...new Set(groupTargets.map(target => target.variantId))];

    // Back to what Liquid rendered before anything is applied, so a store with more stock lifts the
    // ceiling again and a variant the endpoint does not answer for is not left on the last cap.
    groupTargets.forEach(target => target.input && resetToBaseline(target.input, allInputs));

    try {
      const payloads = [];

      for (const ids of chunked(variantIds, BATCH_LIMIT)) {
        const response = await fetchWithTimeout(
          ENDPOINT,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // Never cached: a figure is only true until somebody else buys the unit.
            cache: 'no-store',
            body: JSON.stringify({ items: ids.map(variantId => ({ variantId, ...context })) }),
          },
          signal,
        );
        if (!response.ok) throw new Error(`Availability request failed: ${response.status}`);

        payloads.push(await response.json());
      }

      // The body can arrive after the buyer has moved to another store: aborting stops the request,
      // not a response already in flight.
      if (signal.aborted) return;

      const byVariant = new Map();

      for (const payload of payloads) {
        for (const item of payload?.items ?? []) {
          const variantId = String(item?.variantId ?? '').replace('gid://shopify/ProductVariant/', '');
          if (variantId) byVariant.set(variantId, item);
        }
      }

      const soldOutForms = new Map();
      // Scoped to this group: the desktop/mobile duplicate of one cart line always resolves to the
      // same context, so both land in the same group and share this set — one dispatch per line.
      const dispatchedLines = new Set();

      for (const target of groupTargets) {
        const item = byVariant.get(target.variantId);
        if (!item) continue;

        // A null figure means "no cap": untracked, a variant the merchant lets oversell, or an address
        // no store serves — which is a delivery question, not a stock one.
        if (target.input && typeof item.available === 'number') {
          // An input whose ceiling was unknown has no server-rendered baseline to tighten from, so the
          // store's figure becomes it.
          if (target.input.dataset.capUnknown === 'true') {
            target.input.dataset.max = String(Math.max(0, item.available));
            target.input.max = target.input.dataset.max;
            delete target.input.dataset.capUnknown;
          }

          applyCap(target.input, item.available, allInputs, dispatchedLines, corrections, correctedInputs);
        }

        if (!target.form) continue;

        stampLocation(target.form, context, item.locationId);

        // Gated on what the buyer can actually add after the cap, not on the store's raw figure.
        const nothingToAdd = target.input
          ? parseInt(target.input.max, 10) === 0
          : item.available === 0;

        soldOutForms.set(target.form, (soldOutForms.get(target.form) ?? false) || nothingToAdd);
      }

      // One verdict per form, after every item, and ANY variant with nothing to add gates it: a bundle
      // whose form submits several variants cannot go through when one of them would oversell, and an
      // in-stock sibling must not release the gate the sold-out one set. Folding with `&&` gated only
      // a form whose every variant was out, which is the opposite of that.
      // Before the gate verdicts: releasing a gate re-runs delivery-options' check, which reads the status
      // panel's .out-of-stock marker, so the panel must already reflect this answer or a returning unit
      // leaves Add to cart locked on the previous zero.
      publishRoutedStock(
        context,
        variantIds,
        variantId => byVariant.get(variantId)?.available,
        deliverableOf(byVariant.values()),
      );

      for (const [form, soldOut] of soldOutForms) {
        gateAddToCart(form, soldOut);
      }
    } catch (error) {
      // Fail open, but leave nothing from the previous store behind: the Liquid cap stands, the stamp
      // and the gate go, and checkout validation still blocks the oversell.
      // Only a deliberate abort dies quietly — a newer refresh already owns the page. A TimeoutError
      // is a failure like any other and must reach the cleanup below, or the gate never lifts.
      if (error.name === 'AbortError') return;

      publishRoutedStock(context, variantIds, () => null, undefined);

      for (const target of groupTargets) {
        if (target.input) resetToBaseline(target.input, allInputs);
        if (!target.form) continue;

        stampLocation(target.form, context, null);

        // Fail open, including where no server-rendered cap stands: a card whose picker publishes no
        // caps has no ceiling to fall back to, but holding its Add to cart for the life of the page
        // would make an unreachable proxy an outage. The oversell is caught at checkout, which is
        // where the guarantee has always lived.
        if (target.input) delete target.input.dataset.capUnknown;
        gateAddToCart(target.form, false);
      }
    }
  }

  /**
   * The PDP's delivery status line reads Liquid's store_availabilities, which knows only whether ANY store
   * within the delivery radius stocks the variant — not the store this postcode routes to or its
   * fallbacks. Publishing the routed figure lets it say "out of stock" when this gate has just closed Add
   * to cart for that reason, instead of contradicting it (delivery-stock-status.js).
   *
   * Every requested variant gets an entry, null when there is no figure, so a failed or empty answer
   * replaces an earlier zero rather than leaving it to keep the line on "out of stock".
   */
  function publishRoutedStock(context, variantIds, figureFor, deliverable) {
    if (context.zip === undefined) return;

    const figures = {};

    for (const variantId of variantIds) {
      const available = figureFor(variantId);
      figures[variantId] = typeof available === 'number' ? available : null;
    }

    if (typeof deliverable === 'boolean') knownDeliverable.set(context.zip, deliverable);
    else knownDeliverable.delete(context.zip);

    document.dispatchEvent(
      new CustomEvent('deliveryAddress:routedStock', { detail: { zip: context.zip, figures, deliverable } }),
    );
  }

  /**
   * Asks the app about the postcode the drawer is previewing, before the buyer confirms it. Everything else
   * here reads the saved cookie, which the drawer writes only on confirm, so without this the preview is
   * judged by radius alone and a served postcode past every store's radius can never be confirmed. Only the
   * verdict and figures are published; no cap, stamp or gate changes until the address is saved.
   */
  async function previewVerdict(address) {
    const zip = postcodeOf(address);
    // The saved postcode is the regular refresh's to ask about (the drawer re-previews it on every page load).
    if (!zip || zip === deliveryZip() || knownDeliverable.has(zip)) return;

    const variantId = targets().targeted[0]?.variantId;
    if (!variantId) return;

    previewInFlight?.abort();
    const controller = new AbortController();
    previewInFlight = controller;

    try {
      const response = await fetchWithTimeout(
        ENDPOINT,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify({ items: [{ variantId, zip }] }),
        },
        controller.signal,
      );
      if (!response.ok) throw new Error(`Availability request failed: ${response.status}`);

      const payload = await response.json();
      if (controller.signal.aborted) return;

      const item = (payload?.items ?? [])[0];
      publishRoutedStock({ zip }, [variantId], () => item?.available, deliverableOf(payload?.items ?? []));
    } catch (error) {
      // Silence leaves the preview on the radius check it already shows; nothing to publish.
      if (error.name !== 'AbortError') console.warn('[variant-location-stock] Postcode preview failed:', error);
    } finally {
      if (previewInFlight === controller) previewInFlight = null;
    }
  }

  /**
   * Whether the app offers local delivery to the postcode at all: the same postcode list checkout enforces,
   * so the PDP stops judging delivery by its own distance maths (FPS-807). One postcode per request, so every
   * item carries the same answer. Undefined when the app sent none (an older deploy, a failed call), which
   * leaves the PDP on its previous radius check rather than reading silence as "no".
   */
  function deliverableOf(items) {
    for (const item of items) {
      if (typeof item?.deliverable === 'boolean') return item.deliverable;
    }

    return undefined;
  }

  /**
   * @param {boolean} pending — the page just changed under the buyer (variant, store, cart), so what
   * is on screen no longer describes what they would be adding. Add to cart is held until the answer
   * arrives; without it there is a debounce-plus-network window in which the old variant's cap, gate
   * and stamp are still in force. Not used for the first run on page load, where nothing has changed
   * yet and holding the button would only make the page feel broken.
   */
  function scheduleRefresh(pending = true) {
    // Cancelled the moment the buyer moves, not when the debounced refresh starts: a request that
    // completes inside the debounce window would otherwise stamp the store they just left.
    inFlight?.abort();

    // The stamp goes with it: it names a store this line may have nothing to do with now, and an add
    // to cart carrying it would send checkout validation to the wrong one.
    clearStamps();

    if (pending) {
      for (const form of document.querySelectorAll(PRODUCT_FORMS)) {
        gateAddToCart(form, true);
      }
    }

    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, REFRESH_DELAY);
  }

  document.addEventListener('change', event => {
    const picker = event.target instanceof Element ? event.target.closest(VARIANT_PICKERS) : null;

    if (picker) {
      syncInputToPicker(picker);
      scheduleRefresh(true);
    }
  });

  const onChange = () => scheduleRefresh(true);

  document.addEventListener('location:changed', () => {
    syncPickupAttribute();
    onChange();
  });
  document.addEventListener('deliveryAddress:confirmed', onChange);
  document.addEventListener('deliveryAddress:updated', event => previewVerdict(event.detail));
  document.addEventListener('cart:updated', onChange);
  document.addEventListener('quick-view:loaded', onChange);

  const pubsub = window.FoxTheme?.pubsub;
  if (pubsub?.subscribe) {
    // One variant-change signal, not both: product-info.js publishes through pubsub and dispatches the
    // DOM event back to back.
    if (pubsub.PUB_SUB_EVENTS?.variantChange) {
      pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, onChange);
    }
    // An add to cart from the PDP publishes here rather than dispatching `cart:updated`.
    if (pubsub.PUB_SUB_EVENTS?.cartUpdate) {
      pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, onChange);
    }
    // `setQuantityBoundries` rewrites `max` from the server-rendered total whenever the section is
    // re-fetched, which would otherwise silently undo the per-location cap.
    if (pubsub.PUB_SUB_EVENTS?.quantityBoundries) {
      pubsub.subscribe(pubsub.PUB_SUB_EVENTS.quantityBoundries, onChange);
    }
  } else {
    document.addEventListener('variant:changed', onChange);
  }

  // Some surfaces replace their own markup without publishing anything this file can subscribe to —
  // the selection card swaps its form when the variant changes, quick-view injects one. A replaced
  // form carries only the server-rendered cap and no stamp, so watch for them arriving.
  if (typeof MutationObserver === 'function') {
    const observer = new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          const carriesTarget =
            node.matches?.(`${PRODUCT_FORMS}, ${QUANTITY_INPUTS}`) ||
            node.querySelector?.(`${PRODUCT_FORMS}, ${QUANTITY_INPUTS}`);

          if (carriesTarget) {
            scheduleRefresh(true);
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Deferred, so the DOM is already parsed — no DOMContentLoaded listener to double up with. Nothing
  // has changed yet, so the button is not held for this first pass.
  scheduleRefresh(false);
})();
